import type { AxisLabelsFormatterContextObject } from 'highcharts';

import type { GaugeMetricData } from '@/warranty/domain/entities/gauge-metric';
import {
  type ShadeNumber,
  tailwindColorMap,
} from '@/warranty/view/components/tailwind.colors';

export type Status = 'ok' | 'warn' | 'neutral';

export const statusMeta: Record<
  Status,
  { label: string; bg: string; text: string }
> = {
  ok: { label: 'Supplier-Compliant', bg: '#ecfccb', text: '#065f46' }, // emerald‑50 / emerald‑800
  warn: {
    label: 'Supplier Non-Compliant  — Claim Eligible',
    bg: '#fef3c7',
    text: '#92400e',
  }, // amber‑50 / amber‑800
  neutral: { label: 'N/A', bg: '#e5e7eb', text: '#374151' }, // gray‑200 / gray‑700
};

// Color shade configuration - easy to adjust
const COLOR_SHADES = {
  primary: 200 as ShadeNumber, // Use primary-600 for development state
  gray: 200 as ShadeNumber, // Use gray-200 for inactive state
};

// Get colors from tailwind color map with configured shades
const PRIMARY_COLOR = tailwindColorMap.primary[COLOR_SHADES.primary];
const INACTIVE_COLOR = tailwindColorMap.gray[COLOR_SHADES.gray];

type GaugeTicks = { tickPositions: number[]; tickAmount: number };

const buildGaugeTicks = (min: number, max: number): GaugeTicks => {
  const range = max - min;

  // choose 3 or 5 ticks depending on how "long" the scale is
  const steps = range <= 30 ? 2 : 4; // 0,½,1  vs  0,.25,.5,.75,1
  const stepSize = range / steps;

  const tickPositions = Array.from({ length: steps + 1 }, (_, i) =>
    Number((min + i * stepSize).toFixed(0)),
  );

  return { tickPositions, tickAmount: tickPositions.length };
};

export const getGaugeLayout = (data: GaugeMetricData): Highcharts.Options => {
  const { tickPositions, tickAmount } = buildGaugeTicks(data.min, data.max);

  // Determine colors based on state
  const isActive = data.state === 'active';
  const isDevelopment = data.state === 'development';

  // Generate plotBands based on state
  let plotBands;

  if (isActive) {
    // Active gauges use the original zone definitions
    plotBands = data.zones.map((zone) => ({
      from: zone.from,
      to: zone.to,
      color: zone.color,
      innerRadius: '50%',
      outerRadius: '100%',
    }));
  } else {
    // Inactive or development gauges use a single band from 0 to 100
    const bandColor = isDevelopment ? PRIMARY_COLOR : INACTIVE_COLOR;

    plotBands = [
      {
        from: 0,
        to: 100,
        color: bandColor,
        innerRadius: '50%',
        outerRadius: '100%',
      },
    ];
  }

  const options: Highcharts.Options = {
    chart: {
      type: 'gauge',
      backgroundColor: 'transparent',
      height: 300, // or adjust as needed
      spacingTop: 0,
      spacingBottom: 0,
      spacingLeft: 0,
      spacingRight: 0,
    },
    title: { text: undefined },
    pane: {
      startAngle: -90,
      endAngle: 90,
      size: '100%',
      center: ['50%', '75%'],
      background: [
        {
          backgroundColor: 'transparent',
          innerRadius: '50%',
          outerRadius: '100%',
          shape: 'arc',
        },
      ],
    },
    yAxis: {
      min: data.min,
      max: data.max,
      tickPositions,
      tickAmount,
      tickLength: 4,
      tickColor: '#737373',

      // bring the numbers *inside* the arc so they never overlap neighbours
      labels: {
        distance: -22, // negative = inside
        style: {
          fontSize: '11px',
          fontWeight: 'light',
          color: '#4b5563', // Tailwind slate‑600
        },
        formatter(this: AxisLabelsFormatterContextObject): string {
          return `${this.value}%`;
        },
      },
      plotBands,
      plotLines: isActive
        ? [
            {
              value: data.futurePrediction,
              color: 'black',
              dashStyle: 'Dash',
              width: 2,
              zIndex: 5,
            },
          ]
        : [],
    },
    plotOptions: {
      gauge: {
        dataLabels: {
          enabled: isActive, // Only show data labels when active
          y: -50, // ⬇ move down from pivot (try 15–25)
          borderWidth: 0,
          format: '{y}%',
          style: {
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000000',
          },
        },
      },
    },
    tooltip: {
      enabled: false,
    },
    credits: { enabled: false },
  };

  return options;
};
